// socialCards.js — canvas renderers for the LP portal's downloadable social
// cards. Pure module: every renderer takes the target <canvas> plus any
// portal state (user, badges, pitches) as explicit parameters.
import React from "react";
import { renderToStaticMarkup } from "react-dom/server";
import BadgeIcon from "../icons/BadgeIcon";
import { BADGES } from "../../data/badgeDefinitions";
import { getCurrentQuarter } from "../../services/statsTracking";

// --- Social Card Functions ---
//
// All four cards are 1080×1080 PNGs rendered to a hidden <canvas> and
// downloaded via canvas.toBlob. They share a Millennium Bug aesthetic:
// flat MB-palette blocks (no gradients), 3px ink borders with hard-offset
// shadows, Archivo masthead, Spectral/Hanken body, Hanken bold
// eyebrows, JetBrains Mono numerals. The colorful gnf logo and emoji accents are
// the only image-based content; everything else is typographic.

// ---------- Card palette (hex mirrors of the theme-tokens.css vars) ----------
export const MB_COLORS = {
  ink:           '#141419',
  ink60:         'rgba(20, 20, 25, 0.6)',
  chalk:         '#ffffff',
  paper:         '#faf4e3',
  paperDeep:     '#ede0bd',
  magenta:       '#e93a7d',
  magentaDeep:   '#c21d61',
  magentaSoft:   '#fde0ec',
  grape:         '#6b4fbb',
  grapeDeep:     '#4a2f95',
  aqua:          '#2bb3c4',
  aquaDeep:      '#157b8a',
  aquaSoft:      '#d5f1f4',
  butter:        '#f0c94b',
  butterDeep:    '#c89918',
  butterSoft:    '#fbf1cc',
  tangerine:     '#f28c3b',
  tangerineDeep: '#c66915',
};

export const CARD_FONTS = {
  display:  '"Archivo", "Arial Black", Impact, sans-serif',
  serif:    '"Spectral", Georgia, "Times New Roman", serif',
  content:  '"Hanken Grotesk", "Helvetica Neue", Arial, sans-serif',
  pixel:    '"Hanken Grotesk", "Helvetica Neue", Arial, sans-serif', // retired pixel slot — now the UI sans
  numeral:  '"JetBrains Mono", "Menlo", "Courier New", monospace',
};

// Load an image + resolve to the HTMLImageElement, or null on failure.
export const loadImage = (src) => new Promise((resolve) => {
  const img = new Image();
  img.crossOrigin = 'anonymous';
  img.onload = () => resolve(img);
  img.onerror = () => resolve(null);
  img.src = src;
});

// Serialize a <BadgeIcon> React element to an HTMLImageElement so the canvas
// can draw the SVG glyph directly — replacing the emoji fallback used on the
// badge social card.
export const loadBadgeIconImage = (badgeId, size = 96) => new Promise((resolve) => {
  try {
    const markup = renderToStaticMarkup(<BadgeIcon id={badgeId} size={size} />);
    const ns = markup.includes('xmlns="http://www.w3.org/2000/svg"')
      ? markup
      : markup.replace('<svg ', '<svg xmlns="http://www.w3.org/2000/svg" ');
    const blob = new Blob([ns], { type: 'image/svg+xml' });
    const url = URL.createObjectURL(blob);
    const img = new Image();
    img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
    img.onerror = () => { URL.revokeObjectURL(url); resolve(null); };
    img.src = url;
  } catch {
    resolve(null);
  }
});

// Await web-font readiness so Archivo / Spectral / Hanken
// actually render on the canvas instead of silently falling back.
export const waitForCardFonts = async () => {
  if (typeof document === 'undefined' || !document.fonts) return;
  try {
    // Preload specific size/weight combos the cards need.
    await Promise.all([
      document.fonts.load('700 88px "Archivo"'),
      document.fonts.load('700 60px "Archivo"'),
      document.fonts.load('700 24px "Archivo"'),
      document.fonts.load('600 46px "Spectral"'),
      document.fonts.load('500 24px "Spectral"'),
      document.fonts.load('700 32px "Hanken Grotesk"'),
      document.fonts.load('400 24px "Hanken Grotesk"'),
      document.fonts.load('700 120px "JetBrains Mono"'),
    ]);
    await document.fonts.ready;
  } catch {
    /* font preload failed — fall back to system fonts silently */
  }
};

// Slugify a name to find a matching /assets/lps/<slug>.png portrait.
export const lpSlug = (name) =>
  name ? name.toLowerCase().replace(/\s+/g, '-').replace(/'/g, '') : null;

// Resolve the signed-in user's portrait for the canvas social cards. Prefers
// the admin-uploaded photo (users/{uid}.photoUrl) — matching the members
// directory, edit-panel preview, and chapter pages — then falls back to the
// legacy bundled /assets/lps/<slug>.png, then null so drawPortrait renders the
// initials placeholder. NOTE: photoUrl is a cross-origin Firebase Storage URL;
// loadImage requests it with crossOrigin='anonymous' so the canvas stays
// untainted for toBlob export, which requires CORS on the Storage bucket
// (see cors.json). Without that grant the fetch fails and we fall through to
// the bundled asset / initials, never to a tainted-canvas export error.
export const loadUserPortrait = (u) =>
  u?.photoUrl
    ? loadImage(u.photoUrl)
    : (lpSlug(u?.name) ? loadImage(`/assets/lps/${lpSlug(u.name)}.png`) : Promise.resolve(null));

// Draw the ink top band shared across all four cards: black bar with the
// brand wordmark + tagline in small bold sans, centered.
export const drawMasthead = (ctx, subtitle) => {
  ctx.fillStyle = MB_COLORS.ink;
  ctx.fillRect(0, 0, 1080, 120);

  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';

  ctx.fillStyle = MB_COLORS.chalk;
  ctx.font = `700 24px ${CARD_FONTS.display}`;
  ctx.letterSpacing = '0.5px';
  ctx.fillText('Good Neighbor Fund', 540, 50);

  ctx.fillStyle = MB_COLORS.butter;
  ctx.font = `700 18px ${CARD_FONTS.pixel}`;
  ctx.letterSpacing = '0.5px';
  ctx.fillText(subtitle, 540, 84);

  ctx.letterSpacing = '0px';
  ctx.textBaseline = 'alphabetic';
};

// Draw the ink bottom band (footer): website URL left-aligned, colorful
// logo floats on the right. Accepts the pre-loaded logo image.
export const drawFooter = (ctx, logoImg) => {
  const footerY = 960;
  const footerH = 120;

  ctx.fillStyle = MB_COLORS.ink;
  ctx.fillRect(0, footerY, 1080, footerH);

  // Magenta top stripe
  ctx.fillStyle = MB_COLORS.magenta;
  ctx.fillRect(0, footerY, 1080, 4);

  ctx.fillStyle = MB_COLORS.chalk;
  ctx.font = `500 22px ${CARD_FONTS.numeral}`;
  ctx.letterSpacing = '0.5px';
  ctx.textAlign = 'left';
  ctx.textBaseline = 'middle';
  ctx.fillText('goodneighbor.fund', 60, footerY + 62);

  ctx.letterSpacing = '0px';
  ctx.textBaseline = 'alphabetic';

  if (logoImg) {
    const size = 88;
    ctx.drawImage(logoImg, 1080 - size - 40, footerY + (footerH - size) / 2, size, size);
  }
};

// Draw an ink-bordered rectangle with an offset hard-shadow behind it.
// Mimics the `box-shadow: 3px 3px 0 var(--mb-ink)` pattern.
export const drawShadowRect = (ctx, x, y, w, h, fill, shadowOffset = 6, border = MB_COLORS.ink) => {
  ctx.fillStyle = border;
  ctx.fillRect(x + shadowOffset, y + shadowOffset, w, h);
  ctx.fillStyle = fill;
  ctx.fillRect(x, y, w, h);
  ctx.strokeStyle = border;
  ctx.lineWidth = 3;
  ctx.strokeRect(x, y, w, h);
};

// Draw a circular portrait at (cx, cy) with radius r and a 4px ink ring.
// Falls back to a solid magenta square with initials if no photo loads.
export const drawPortrait = (ctx, cx, cy, r, photoImg, name) => {
  // Ink ring
  ctx.save();
  ctx.strokeStyle = MB_COLORS.ink;
  ctx.lineWidth = 4;
  ctx.beginPath();
  ctx.arc(cx, cy, r + 3, 0, Math.PI * 2);
  ctx.stroke();
  ctx.restore();

  ctx.save();
  ctx.beginPath();
  ctx.arc(cx, cy, r, 0, Math.PI * 2);
  ctx.clip();

  if (photoImg) {
    // Cover-crop to a centered square before drawing — stretching a
    // non-square upload into the circle distorts faces. Mirrors the
    // object-fit: cover treatment photos get in the members directory.
    const iw = photoImg.naturalWidth || photoImg.width;
    const ih = photoImg.naturalHeight || photoImg.height;
    const side = Math.min(iw, ih);
    const sx = (iw - side) / 2;
    const sy = (ih - side) / 2;
    ctx.drawImage(photoImg, sx, sy, side, side, cx - r, cy - r, r * 2, r * 2);
  } else {
    // Initial-based fallback on paper background
    ctx.fillStyle = MB_COLORS.paper;
    ctx.fillRect(cx - r, cy - r, r * 2, r * 2);
    const initials = (name || 'LP')
      .split(' ')
      .map((w) => w[0])
      .filter(Boolean)
      .slice(0, 2)
      .join('')
      .toUpperCase() || 'LP';
    ctx.fillStyle = MB_COLORS.magenta;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.font = `700 ${Math.round(r * 0.9)}px ${CARD_FONTS.display}`;
    ctx.fillText(initials, cx, cy + 2);
    ctx.textBaseline = 'alphabetic';
  }
  ctx.restore();
};

// ---------- 1. Welcome Card — new LP introduces themselves ----------
export const drawWelcomeCard = async (canvas, user) => {
  await waitForCardFonts();
  const ctx = canvas.getContext('2d');

  // Paper base fill for full 1080×1080
  ctx.fillStyle = MB_COLORS.paper;
  ctx.fillRect(0, 0, 1080, 1080);

  drawMasthead(ctx, 'Welcome to the chapter');

  // Pre-load photo + logo in parallel so drawing is deterministic.
  const [photoImg, logoImg] = await Promise.all([
    loadUserPortrait(user),
    loadImage('/assets/gnf-logo.png'),
  ]);

  // Portrait at center
  drawPortrait(ctx, 540, 300, 120, photoImg, user?.name);

  // Eyebrow + name
  ctx.textAlign = 'center';
  ctx.fillStyle = MB_COLORS.magenta;
  ctx.font = `700 20px ${CARD_FONTS.pixel}`;
  ctx.letterSpacing = '0.5px';
  ctx.fillText(`Limited Partner · ${user?.chapter || 'Chapter'}`, 540, 470);
  ctx.letterSpacing = '0px';

  ctx.fillStyle = MB_COLORS.ink;
  ctx.font = `700 60px ${CARD_FONTS.display}`;
  ctx.fillText(user?.name || 'New Neighbor', 540, 545);

  // Masthead headline — Archivo all-caps, second line magenta
  ctx.fillStyle = MB_COLORS.ink;
  ctx.font = `700 56px ${CARD_FONTS.display}`;
  ctx.fillText("I'm backing founders", 540, 660);
  ctx.font = `700 56px ${CARD_FONTS.display}`;
  ctx.fillStyle = MB_COLORS.magenta;
  ctx.fillText('in my neighborhood.', 540, 730);

  // Supporting body
  ctx.fillStyle = MB_COLORS.ink;
  ctx.font = `400 22px ${CARD_FONTS.content}`;
  ctx.fillText('$1,000 micro-grants · No pitch deck · No equity · Just belief', 540, 790);

  // Tag row
  const tags = ['Belief capital', 'Since 2023', 'Volunteer-led'];
  const tagW = 180;
  const tagH = 50;
  let tagX = 540 - ((tags.length * tagW + (tags.length - 1) * 14) / 2);
  tags.forEach((t) => {
    drawShadowRect(ctx, tagX, 830, tagW, tagH, MB_COLORS.chalk, 4);
    ctx.fillStyle = MB_COLORS.ink;
    ctx.font = `700 15px ${CARD_FONTS.pixel}`;
    ctx.letterSpacing = '0.5px';
    ctx.textBaseline = 'middle';
    ctx.fillText(t, tagX + tagW / 2, 855);
    ctx.textBaseline = 'alphabetic';
    ctx.letterSpacing = '0px';
    tagX += tagW + 14;
  });

  drawFooter(ctx, logoImg);
};

// ---------- 2. Badge Card — achievement showcase ----------
export const drawBadgeCard = async (canvas, user, userBadges) => {
  await waitForCardFonts();
  const ctx = canvas.getContext('2d');

  // Grape field
  ctx.fillStyle = MB_COLORS.grape;
  ctx.fillRect(0, 0, 1080, 1080);

  drawMasthead(ctx, `${(user?.chapter || 'Chapter')} · Badges`);

  const [photoImg, logoImg] = await Promise.all([
    loadUserPortrait(user),
    loadImage('/assets/gnf-logo.png'),
  ]);

  // Small portrait + name in a row
  drawPortrait(ctx, 180, 220, 70, photoImg, user?.name);

  ctx.textAlign = 'left';
  ctx.fillStyle = MB_COLORS.butter;
  ctx.font = `700 18px ${CARD_FONTS.pixel}`;
  ctx.letterSpacing = '0.5px';
  ctx.fillText('Limited Partner', 280, 192);
  ctx.letterSpacing = '0px';

  ctx.fillStyle = MB_COLORS.chalk;
  ctx.font = `700 48px ${CARD_FONTS.display}`;
  ctx.fillText(user?.name || 'Achievement Hunter', 280, 252);

  // Big numeral: badge count
  ctx.textAlign = 'center';
  ctx.fillStyle = MB_COLORS.butter;
  ctx.font = `700 180px ${CARD_FONTS.numeral}`;
  ctx.fillText(String(userBadges.length), 540, 470);

  ctx.fillStyle = MB_COLORS.chalk;
  ctx.font = `700 22px ${CARD_FONTS.pixel}`;
  ctx.letterSpacing = '0.5px';
  ctx.fillText('Badges unlocked', 540, 514);
  ctx.letterSpacing = '0px';

  // Recent-badges grid (up to 4 badges, 2×2, on a chalk card with ink shadow)
  const recentBadges = userBadges
    .filter((b) => b.earnedAt || b.earnedDate)
    .sort((a, b) => {
      const dA = new Date(a.earnedAt || a.earnedDate || 0).getTime();
      const dB = new Date(b.earnedAt || b.earnedDate || 0).getTime();
      return dB - dA;
    })
    .slice(0, 4);

  if (recentBadges.length > 0) {
    const gridX = 120;
    const gridY = 560;
    const gridW = 840;
    const gridH = 340;

    // Card background
    drawShadowRect(ctx, gridX, gridY, gridW, gridH, MB_COLORS.chalk, 8);

    // Eyebrow on card
    ctx.textAlign = 'left';
    ctx.fillStyle = MB_COLORS.magenta;
    ctx.font = `700 18px ${CARD_FONTS.pixel}`;
    ctx.letterSpacing = '0.5px';
    ctx.fillText('Recently earned', gridX + 24, gridY + 38);
    ctx.letterSpacing = '0px';

    const cell = { w: 180, h: 130, gapX: 24, gapY: 18 };
    const rowCount = Math.ceil(recentBadges.length / 4);
    const cellsPerRow = Math.ceil(recentBadges.length / rowCount);
    const startX = gridX + (gridW - (cellsPerRow * cell.w + (cellsPerRow - 1) * cell.gapX)) / 2;
    const startY = gridY + 64;

    const iconImgs = await Promise.all(
      recentBadges.map((b) => loadBadgeIconImage(b.id || b.badgeId, 128))
    );

    recentBadges.forEach((badge, i) => {
      const row = Math.floor(i / cellsPerRow);
      const col = i % cellsPerRow;
      const bx = startX + col * (cell.w + cell.gapX);
      const by = startY + row * (cell.h + cell.gapY);

      const badgeData = BADGES[badge.id || badge.badgeId];
      if (!badgeData) return;

      // Cell background (butter-soft)
      ctx.fillStyle = MB_COLORS.butterSoft;
      ctx.fillRect(bx, by, cell.w, cell.h);
      ctx.strokeStyle = MB_COLORS.ink;
      ctx.lineWidth = 2;
      ctx.strokeRect(bx, by, cell.w, cell.h);

      // BadgeIcon SVG pictogram
      const iconSize = 64;
      const iconImg = iconImgs[i];
      if (iconImg) {
        ctx.drawImage(iconImg, bx + (cell.w - iconSize) / 2, by + 14, iconSize, iconSize);
      }

      // Name (wrap if long) — strip the leading emoji glyph from stored name.
      ctx.textAlign = 'center';
      ctx.fillStyle = MB_COLORS.ink;
      ctx.font = `700 13px ${CARD_FONTS.content}`;
      const words = badgeData.name.split(' ').slice(1).join(' ').split(' ');
      let line = '';
      const lines = [];
      words.forEach((word) => {
        const test = line + word + ' ';
        if (ctx.measureText(test).width > cell.w - 16 && line !== '') {
          lines.push(line.trim());
          line = word + ' ';
        } else {
          line = test;
        }
      });
      lines.push(line.trim());
      lines.slice(0, 2).forEach((txt, idx) => {
        ctx.fillText(txt, bx + cell.w / 2, by + 96 + idx * 18);
      });
    });
  }

  drawFooter(ctx, logoImg);
};

// ---------- 3. Chapter Stats Card — impact numbers ----------
export const drawChapterStatsCard = async (canvas, user, isAdmin, adminPitches, lpPitches) => {
  await waitForCardFonts();
  const ctx = canvas.getContext('2d');

  ctx.fillStyle = MB_COLORS.paper;
  ctx.fillRect(0, 0, 1080, 1080);

  drawMasthead(ctx, `${user?.chapter || 'Chapter'} · Impact`);

  const logoImg = await loadImage('/assets/gnf-logo.png');

  // Calculate all-time stats — admin uses adminPitches, LP uses lpPitches
  const pitchesToUse = isAdmin ? adminPitches : lpPitches;
  const chapterPitches = (pitchesToUse || []).filter((p) => p.chapter === user?.chapter);
  const grantsAwarded = chapterPitches.filter((p) => p.isWinner).length;
  const dollarsAwarded = grantsAwarded * 1000;

  // Eyebrow + chapter name
  ctx.textAlign = 'center';
  ctx.fillStyle = MB_COLORS.magenta;
  ctx.font = `700 24px ${CARD_FONTS.pixel}`;
  ctx.letterSpacing = '0.5px';
  ctx.fillText('Chapter impact · Since 2023', 540, 204);
  ctx.letterSpacing = '0px';

  ctx.fillStyle = MB_COLORS.ink;
  ctx.font = `700 64px ${CARD_FONTS.display}`;
  ctx.fillText(user?.chapter || 'Chapter', 540, 280);

  // Two stat cards side-by-side
  const statsY = 340;
  const cardW = 420;
  const cardH = 220;
  const leftX = 80;
  const rightX = 580;

  // Left: grants count (magenta)
  drawShadowRect(ctx, leftX, statsY, cardW, cardH, MB_COLORS.magenta, 8);
  ctx.fillStyle = MB_COLORS.chalk;
  ctx.font = `700 20px ${CARD_FONTS.pixel}`;
  ctx.letterSpacing = '0.5px';
  ctx.textAlign = 'left';
  ctx.fillText('Businesses funded', leftX + 24, statsY + 48);
  ctx.letterSpacing = '0px';
  ctx.fillStyle = MB_COLORS.butter;
  ctx.font = `700 140px ${CARD_FONTS.numeral}`;
  ctx.textAlign = 'right';
  ctx.fillText(String(grantsAwarded), leftX + cardW - 24, statsY + 180);

  // Right: dollars awarded (aqua)
  drawShadowRect(ctx, rightX, statsY, cardW, cardH, MB_COLORS.aqua, 8);
  ctx.fillStyle = MB_COLORS.ink;
  ctx.font = `700 20px ${CARD_FONTS.pixel}`;
  ctx.letterSpacing = '0.5px';
  ctx.textAlign = 'left';
  ctx.fillText('Awarded in grants', rightX + 24, statsY + 48);
  ctx.letterSpacing = '0px';
  ctx.fillStyle = MB_COLORS.ink;
  ctx.font = `700 120px ${CARD_FONTS.numeral}`;
  ctx.textAlign = 'right';
  ctx.fillText(`$${(dollarsAwarded / 1000).toFixed(dollarsAwarded % 1000 === 0 ? 0 : 1)}K`, rightX + cardW - 24, statsY + 180);

  // Pull quote
  ctx.textAlign = 'center';
  ctx.fillStyle = MB_COLORS.ink;
  ctx.font = `600 46px ${CARD_FONTS.serif}`;
  ctx.fillText('"Funded by belief.', 540, 670);
  ctx.fillText('Fueled by neighbors."', 540, 720);

  // CTA bar (magenta)
  drawShadowRect(ctx, 80, 770, 920, 120, MB_COLORS.magenta, 8);
  ctx.fillStyle = MB_COLORS.chalk;
  ctx.font = `700 20px ${CARD_FONTS.pixel}`;
  ctx.letterSpacing = '0.5px';
  ctx.fillText('Got an idea?', 540, 814);
  ctx.letterSpacing = '0px';
  ctx.font = `700 36px ${CARD_FONTS.content}`;
  ctx.fillText('Apply for a $1,000 micro-grant today.', 540, 862);

  drawFooter(ctx, logoImg);
};

// ---------- 4. Recruitment Card — LP personal brand ----------
export const drawRecruitmentCard = async (canvas, user) => {
  await waitForCardFonts();
  const ctx = canvas.getContext('2d');

  // Magenta field
  ctx.fillStyle = MB_COLORS.magenta;
  ctx.fillRect(0, 0, 1080, 1080);

  drawMasthead(ctx, 'Join the LP Network');

  const [photoImg, logoImg] = await Promise.all([
    loadUserPortrait(user),
    loadImage('/assets/gnf-logo.png'),
  ]);

  // Portrait, large
  drawPortrait(ctx, 540, 310, 120, photoImg, user?.name);

  ctx.textAlign = 'center';
  ctx.fillStyle = MB_COLORS.butter;
  ctx.font = `700 20px ${CARD_FONTS.pixel}`;
  ctx.letterSpacing = '0.5px';
  ctx.fillText(`Limited Partner · ${user?.chapter || 'Chapter'}`, 540, 475);
  ctx.letterSpacing = '0px';

  ctx.fillStyle = MB_COLORS.chalk;
  ctx.font = `700 56px ${CARD_FONTS.display}`;
  ctx.fillText(user?.name || 'LP Name', 540, 550);

  // Masthead headline — Archivo all-caps, second line butter
  ctx.font = `700 52px ${CARD_FONTS.display}`;
  ctx.fillText('I fund neighbors', 540, 640);
  ctx.font = `700 52px ${CARD_FONTS.display}`;
  ctx.fillStyle = MB_COLORS.butter;
  ctx.fillText('before the VCs do.', 540, 700);

  // CTA — butter rect with ink text
  drawShadowRect(ctx, 90, 770, 900, 140, MB_COLORS.butter, 8);
  ctx.fillStyle = MB_COLORS.ink;
  ctx.font = `700 20px ${CARD_FONTS.pixel}`;
  ctx.letterSpacing = '0.5px';
  ctx.fillText('Want in?', 540, 814);
  ctx.letterSpacing = '0px';
  ctx.font = `700 34px ${CARD_FONTS.content}`;
  ctx.fillText("DM me — we're looking for neighbors like you.", 540, 872);

  drawFooter(ctx, logoImg);
};

// ---------- Win95 chrome helpers (shared by approval + application cards) ----------
export const drawWindowChrome = (ctx, x, y, w, h, titleText) => {
  // Outer paper window with hard ink shadow
  drawShadowRect(ctx, x, y, w, h, MB_COLORS.chalk, 10);

  // Titlebar band
  const tbH = 60;
  ctx.fillStyle = MB_COLORS.paperDeep;
  ctx.fillRect(x, y, w, tbH);
  ctx.fillStyle = MB_COLORS.ink;
  ctx.fillRect(x, y + tbH - 2, w, 2);

  // Titlebar text
  ctx.textBaseline = 'middle';
  ctx.textAlign = 'left';
  ctx.fillStyle = MB_COLORS.ink;
  ctx.font = `700 22px ${CARD_FONTS.content}`;
  ctx.fillText(titleText, x + 36, y + tbH / 2);

  // Faux window-control buttons (right-aligned: _ □ ×)
  const symbols = ['_', '□', '×'];
  const btnW = 28; const btnH = 26; const gap = 6;
  const totalW = symbols.length * btnW + (symbols.length - 1) * gap;
  let bx = x + w - 24 - totalW;
  const by = y + (tbH - btnH) / 2;
  symbols.forEach((sym) => {
    ctx.fillStyle = MB_COLORS.paper;
    ctx.fillRect(bx, by, btnW, btnH);
    ctx.strokeStyle = MB_COLORS.ink;
    ctx.lineWidth = 2;
    ctx.strokeRect(bx, by, btnW, btnH);
    ctx.fillStyle = MB_COLORS.ink;
    ctx.font = `700 16px ${CARD_FONTS.content}`;
    ctx.textAlign = 'center';
    ctx.fillText(sym, bx + btnW / 2, by + btnH / 2 + 1);
    bx += btnW + gap;
  });

  ctx.textBaseline = 'alphabetic';
  ctx.textAlign = 'left';
};

// ---------- 5. Approval Dialog Card — Win95 "grant approved" notification ----------
export const drawApprovalDialogCard = async (canvas, user) => {
  await waitForCardFonts();
  const ctx = canvas.getContext('2d');

  // Soft pink desktop field
  ctx.fillStyle = MB_COLORS.magentaSoft;
  ctx.fillRect(0, 0, 1080, 1080);

  drawMasthead(ctx, 'Belief, in writing');

  const [photoImg, logoImg] = await Promise.all([
    loadUserPortrait(user),
    loadImage('/assets/gnf-logo.png'),
  ]);

  // Dialog window
  const winX = 60, winY = 200, winW = 960, winH = 700;
  drawWindowChrome(ctx, winX, winY, winW, winH, 'Good Neighbor Fund — Notification');

  // Award icon (butter trophy block) on the left
  drawShadowRect(ctx, 130, 320, 140, 140, MB_COLORS.butter, 8);
  ctx.strokeStyle = MB_COLORS.ink;
  ctx.lineWidth = 12;
  ctx.lineJoin = 'miter';
  ctx.beginPath();
  ctx.moveTo(160, 395);
  ctx.lineTo(195, 432);
  ctx.lineTo(255, 360);
  ctx.stroke();

  // Eyebrow
  ctx.textAlign = 'left';
  ctx.fillStyle = MB_COLORS.magentaDeep;
  ctx.font = `700 18px ${CARD_FONTS.pixel}`;
  ctx.letterSpacing = '0.5px';
  ctx.fillText('Grant approved · System message', 320, 345);
  ctx.letterSpacing = '0px';

  // Masthead headline — Archivo all-caps, the middle phrase in magenta
  const hLineY1 = 440;
  const hLineY2 = 540;
  const hSize = 72;
  ctx.fillStyle = MB_COLORS.ink;
  ctx.font = `700 ${hSize}px ${CARD_FONTS.display}`;
  ctx.fillText('$1,000 ', 320, hLineY1);
  const part1W = ctx.measureText('$1,000 ').width;
  ctx.font = `700 ${hSize}px ${CARD_FONTS.display}`;
  ctx.fillStyle = MB_COLORS.magenta;
  ctx.fillText('for your', 320 + part1W, hLineY1);
  ctx.font = `700 ${hSize}px ${CARD_FONTS.display}`;
  ctx.fillStyle = MB_COLORS.ink;
  ctx.fillText('big idea.', 320, hLineY2);

  // Subhead
  ctx.fillStyle = MB_COLORS.ink60;
  ctx.font = `400 26px ${CARD_FONTS.content}`;
  ctx.fillText('No pitch deck. No equity. Just belief.', 320, 610);

  // LP byline (portrait + name + chapter) — anchors the card to the sharer
  const bylineY = 730;
  drawPortrait(ctx, 165, bylineY, 38, photoImg, user?.name);
  ctx.textAlign = 'left';
  ctx.fillStyle = MB_COLORS.magentaDeep;
  ctx.font = `700 16px ${CARD_FONTS.pixel}`;
  ctx.letterSpacing = '0.5px';
  ctx.fillText('Shared by', 220, bylineY - 14);
  ctx.letterSpacing = '0px';
  ctx.fillStyle = MB_COLORS.ink;
  ctx.font = `700 26px ${CARD_FONTS.content}`;
  ctx.fillText(user?.name || 'A Limited Partner', 220, bylineY + 14);
  ctx.fillStyle = MB_COLORS.ink60;
  ctx.font = `400 18px ${CARD_FONTS.content}`;
  ctx.fillText(`${user?.chapter || 'GNF'} · LP`, 220, bylineY + 40);

  // Button row (right-aligned inside dialog)
  const btnY = 800;
  drawShadowRect(ctx, 600, btnY, 170, 60, MB_COLORS.paper, 4);
  ctx.fillStyle = MB_COLORS.ink;
  ctx.font = `700 16px ${CARD_FONTS.content}`;
  ctx.textAlign = 'center';
  ctx.letterSpacing = '0.5px';
  ctx.textBaseline = 'middle';
  ctx.fillText('Close', 600 + 85, btnY + 30);

  drawShadowRect(ctx, 790, btnY, 200, 60, MB_COLORS.magenta, 4);
  ctx.fillStyle = MB_COLORS.chalk;
  ctx.fillText('APPLY NOW →', 790 + 100, btnY + 30);
  ctx.letterSpacing = '0px';
  ctx.textBaseline = 'alphabetic';

  drawFooter(ctx, logoImg);
};

// ---------- 6. Application Form Card — Win95 "step 1 of 3" form ----------
export const drawApplicationFormCard = async (canvas, user) => {
  await waitForCardFonts();
  const ctx = canvas.getContext('2d');

  // Soft aqua desktop field
  ctx.fillStyle = MB_COLORS.aquaSoft;
  ctx.fillRect(0, 0, 1080, 1080);

  drawMasthead(ctx, 'Apply in one sentence');

  const [photoImg, logoImg] = await Promise.all([
    loadUserPortrait(user),
    loadImage('/assets/gnf-logo.png'),
  ]);

  const winX = 60, winY = 200, winW = 960, winH = 700;
  drawWindowChrome(ctx, winX, winY, winW, winH, 'GNF Application — Step 1 of 3');

  // Step bar — left label
  ctx.textAlign = 'left';
  ctx.fillStyle = MB_COLORS.ink60;
  ctx.font = `700 18px ${CARD_FONTS.pixel}`;
  ctx.letterSpacing = '0.5px';
  ctx.fillText('Step 1 of 3 · The idea', 130, 320);
  ctx.letterSpacing = '0px';

  // Progress dots (right)
  const dotSize = 18;
  const dotGap = 10;
  const dotsRightX = 950;
  const dotsY = 308;
  for (let i = 0; i < 3; i++) {
    const dx = dotsRightX - (3 - i) * (dotSize + dotGap);
    ctx.fillStyle = i === 0 ? MB_COLORS.magenta : MB_COLORS.paper;
    ctx.fillRect(dx, dotsY, dotSize, dotSize);
    ctx.strokeStyle = MB_COLORS.ink;
    ctx.lineWidth = 2;
    ctx.strokeRect(dx, dotsY, dotSize, dotSize);
  }

  // Editorial label: "What's your big idea?"
  ctx.textAlign = 'left';
  ctx.fillStyle = MB_COLORS.ink;
  ctx.font = `700 64px ${CARD_FONTS.display}`;
  ctx.fillText('What’s your ', 130, 440);
  const lblW = ctx.measureText('What’s your ').width;
  ctx.font = `700 64px ${CARD_FONTS.display}`;
  ctx.fillStyle = MB_COLORS.magenta;
  ctx.fillText('big idea?', 130 + lblW, 440);

  // Sunken input field with typed example + cursor
  drawShadowRect(ctx, 130, 510, 820, 110, MB_COLORS.paper, 6);
  ctx.fillStyle = MB_COLORS.ink;
  ctx.font = `400 30px ${CARD_FONTS.content}`;
  ctx.textBaseline = 'middle';
  const fillText = 'I want to start a mobile bike-repair shop';
  ctx.fillText(fillText, 156, 565);
  // Solid cursor (rendered statically — safe for canvas export)
  const cursorX = 156 + ctx.measureText(fillText).width + 6;
  ctx.fillStyle = MB_COLORS.ink;
  ctx.fillRect(cursorX, 542, 4, 46);
  ctx.textBaseline = 'alphabetic';

  // Helper text + counter
  ctx.fillStyle = MB_COLORS.ink60;
  ctx.font = `400 22px ${CARD_FONTS.content}`;
  ctx.fillText('No pitch deck required. No equity. Just belief.', 130, 660);
  ctx.textAlign = 'right';
  ctx.font = `400 18px ${CARD_FONTS.numeral}`;
  ctx.fillText('1 / 1 sentence', 950, 660);

  // LP byline (left) + buttons (right) on the same row
  const btnY = 800;
  const bylineCY = btnY + 30;
  drawPortrait(ctx, 170, bylineCY, 36, photoImg, user?.name);
  ctx.textAlign = 'left';
  ctx.fillStyle = MB_COLORS.magentaDeep;
  ctx.font = `700 16px ${CARD_FONTS.pixel}`;
  ctx.letterSpacing = '0.5px';
  ctx.textBaseline = 'alphabetic';
  ctx.fillText('Shared by', 220, bylineCY - 14);
  ctx.letterSpacing = '0px';
  ctx.fillStyle = MB_COLORS.ink;
  ctx.font = `600 22px ${CARD_FONTS.serif}`;
  ctx.fillText(`${user?.name || 'A Limited Partner'} — ${user?.chapter || 'GNF'} LP`, 220, bylineCY + 14);

  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  drawShadowRect(ctx, 620, btnY, 150, 60, MB_COLORS.paper, 4);
  ctx.fillStyle = MB_COLORS.ink60;
  ctx.font = `700 16px ${CARD_FONTS.content}`;
  ctx.letterSpacing = '0.5px';
  ctx.fillText('← BACK', 620 + 75, btnY + 30);

  drawShadowRect(ctx, 790, btnY, 200, 60, MB_COLORS.magenta, 4);
  ctx.fillStyle = MB_COLORS.chalk;
  ctx.fillText('NEXT →', 790 + 100, btnY + 30);
  ctx.letterSpacing = '0px';
  ctx.textBaseline = 'alphabetic';
  ctx.textAlign = 'left';

  drawFooter(ctx, logoImg);
};

// ---------- 7. Invite Card — recruit the next Limited Partner ----------
// Mirrors the "Become a Limited Partner" recruiting flyer as a 1080×1080
// share card: a personal "Join me as a Limited Partner" headline (the sharer's
// name + chapter auto-filled), a 2×2 color-blocked stat grid carrying the four
// hard facts ($500/yr · $1,000 grants · 4× dinners · ~1hr a month), and a
// single ink CTA bar pointing to the application.
export const drawInviteCard = async (canvas, user) => {
  await waitForCardFonts();
  const ctx = canvas.getContext('2d');

  // Paper base
  ctx.fillStyle = MB_COLORS.paper;
  ctx.fillRect(0, 0, 1080, 1080);

  drawMasthead(ctx, 'Neighbors funding neighbors');

  const [photoImg, logoImg] = await Promise.all([
    loadUserPortrait(user),
    loadImage('/assets/gnf-logo.png'),
  ]);

  const name = user?.name || 'A Neighbor';
  const chapter = user?.chapter || 'Your Chapter';

  // Eyebrow
  ctx.textAlign = 'left';
  ctx.textBaseline = 'alphabetic';
  ctx.fillStyle = MB_COLORS.magentaDeep;
  ctx.font = `700 18px ${CARD_FONTS.pixel}`;
  ctx.letterSpacing = '0.5px';
  ctx.fillText('An invitation to give together', 60, 168);
  ctx.letterSpacing = '0px';

  // Masthead headline — Archivo all-caps, the personal "ME" in magenta
  const hSize = 76;
  ctx.fillStyle = MB_COLORS.ink;
  ctx.font = `700 ${hSize}px ${CARD_FONTS.display}`;
  const hy1 = 248;
  let hx = 60;
  ctx.fillText('Join ', hx, hy1);
  hx += ctx.measureText('Join ').width;
  ctx.font = `700 ${hSize}px ${CARD_FONTS.display}`;
  ctx.fillStyle = MB_COLORS.magenta;
  ctx.fillText('me', hx, hy1);
  hx += ctx.measureText('me').width;
  ctx.font = `700 ${hSize}px ${CARD_FONTS.display}`;
  ctx.fillStyle = MB_COLORS.ink;
  ctx.fillText(' as a', hx, hy1);
  ctx.fillText('Limited Partner', 60, hy1 + 70);

  // Portrait — top-right of the content field
  drawPortrait(ctx, 940, 232, 72, photoImg, name);

  // Byline — "YOUR NEIGHBOR" / name / chapter (chapter always shown)
  ctx.textAlign = 'left';
  ctx.fillStyle = MB_COLORS.grape;
  ctx.font = `700 15px ${CARD_FONTS.pixel}`;
  ctx.letterSpacing = '0.5px';
  ctx.fillText('Your neighbor', 60, 352);
  ctx.letterSpacing = '0px';

  // Name — auto-shrink only down to a still-legible floor for long names
  let nameSize = 34;
  ctx.fillStyle = MB_COLORS.ink;
  ctx.font = `600 ${nameSize}px ${CARD_FONTS.content}`;
  while (ctx.measureText(name).width > 660 && nameSize > 26) {
    nameSize -= 1;
    ctx.font = `600 ${nameSize}px ${CARD_FONTS.content}`;
  }
  ctx.fillText(name, 60, 388);

  ctx.fillStyle = MB_COLORS.ink60;
  ctx.font = `400 20px ${CARD_FONTS.content}`;
  ctx.fillText(chapter, 60, 414);

  // Tagline — right column under the portrait, right-aligned so it stays clear
  // of long names on the left and never runs off the right edge.
  ctx.textAlign = 'right';
  ctx.fillStyle = MB_COLORS.ink;
  ctx.font = `400 26px ${CARD_FONTS.content}`;
  ctx.fillText('Pool $500 a year.', 1014, 366);
  ctx.fillText('Back founders, together.', 1014, 402);
  ctx.textAlign = 'left';

  // 2×2 color-blocked stat grid — the hero
  const gridX = 60, gridY = 438;
  const cellW = 465, cellH = 186, gap = 26;
  const cells = [
    { fill: MB_COLORS.magenta, fg: MB_COLORS.chalk, num: '$500',   cap: 'Per year · Tax-deductible' },
    { fill: MB_COLORS.aqua,    fg: MB_COLORS.ink,   num: '$1,000', cap: 'Grants you award together' },
    { fill: MB_COLORS.grape,   fg: MB_COLORS.chalk, num: '4x',     cap: 'Dinners a year · Best meeting' },
    { fill: MB_COLORS.butter,  fg: MB_COLORS.ink,   num: '~1 hr',  cap: 'A month rating pitches' },
  ];

  cells.forEach((c, i) => {
    const col = i % 2, row = Math.floor(i / 2);
    const cx = gridX + col * (cellW + gap);
    const cy = gridY + row * (cellH + gap);
    drawShadowRect(ctx, cx, cy, cellW, cellH, c.fill, 8);

    // Big numeral — right-aligned, width-capped so "$1,000" never clips
    ctx.textAlign = 'right';
    ctx.textBaseline = 'alphabetic';
    ctx.fillStyle = c.fg;
    let numFont = 92;
    ctx.font = `700 ${numFont}px ${CARD_FONTS.numeral}`;
    while (ctx.measureText(c.num).width > cellW - 56 && numFont > 56) {
      numFont -= 4;
      ctx.font = `700 ${numFont}px ${CARD_FONTS.numeral}`;
    }
    ctx.fillText(c.num, cx + cellW - 26, cy + 104);

    // Caption — small bold sans, bottom-left, wrapped to max 2 lines
    ctx.textAlign = 'left';
    ctx.fillStyle = c.fg;
    ctx.letterSpacing = '1px';
    ctx.font = `700 15px ${CARD_FONTS.pixel}`;
    const capMax = cellW - 52;
    const words = c.cap.split(' ');
    const lines = [];
    let cur = '';
    words.forEach((w) => {
      const test = cur ? cur + ' ' + w : w;
      if (ctx.measureText(test).width > capMax && cur) { lines.push(cur); cur = w; }
      else { cur = test; }
    });
    if (cur) lines.push(cur);
    const capLines = lines.slice(0, 2);
    let capY = cy + cellH - 26 - (capLines.length - 1) * 22;
    capLines.forEach((ln) => { ctx.fillText(ln, cx + 26, capY); capY += 22; });
    ctx.letterSpacing = '0px';
  });

  // CTA bar — single ink block: pixel label left, application URL right
  const ctaX = 60, ctaY = 856, ctaW = 960, ctaH = 78;
  drawShadowRect(ctx, ctaX, ctaY, ctaW, ctaH, MB_COLORS.ink, 8);

  ctx.textBaseline = 'middle';
  ctx.textAlign = 'left';
  ctx.fillStyle = MB_COLORS.butter;
  ctx.font = `700 18px ${CARD_FONTS.pixel}`;
  ctx.letterSpacing = '0.5px';
  ctx.fillText('YOUR SEAT AT THE TABLE  →', ctaX + 28, ctaY + ctaH / 2 + 1);
  ctx.letterSpacing = '0px';
  ctx.textAlign = 'right';
  ctx.fillStyle = MB_COLORS.chalk;
  ctx.font = `600 26px ${CARD_FONTS.content}`;
  ctx.fillText('goodneighbor.fund/lp-application', ctaX + ctaW - 28, ctaY + ctaH / 2 + 1);
  ctx.textBaseline = 'alphabetic';
  ctx.textAlign = 'left';

  drawFooter(ctx, logoImg);
};

// ---------- 8. The $1,000 Check — recruit the next founder application ----------
// A re-creation of the GNF "big check" made out to "Your Business Here": a
// tilted check with a thick aqua security border, the logo (no wordmark) in
// the bank-name corner, today's DATE, a boxed $1,000.00, "Belief capital" memo
// and a "Chapter Director" signature. The masthead, DATE and CTA auto-fill
// from the current quarter so the card never goes stale — the headline reframes
// the grant as a check already written, waiting only for the founder's name.
export const drawCheckCard = async (canvas) => {
  await waitForCardFonts();
  const ctx = canvas.getContext('2d');

  // Auto data: today's date + the current quarter's application deadline.
  // quarterLabel uses the canonical getCurrentQuarter(); the deadline is the
  // last calendar day of that quarter.
  const now = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'];
  const q = Math.floor(now.getMonth() / 3);
  const deadline = new Date(now.getFullYear(), q * 3 + 3, 0);
  const dateStr = `${pad(now.getMonth() + 1)}/${pad(now.getDate())}/${now.getFullYear()}`;
  const quarterLabel = getCurrentQuarter();
  const deadlineStr = `${MONTHS[deadline.getMonth()]} ${deadline.getDate()}, ${deadline.getFullYear()}`;

  // Paper base
  ctx.fillStyle = MB_COLORS.paper;
  ctx.fillRect(0, 0, 1080, 1080);

  drawMasthead(ctx, `Apply by ${deadlineStr}`);
  const logoImg = await loadImage('/assets/gnf-logo.png');

  // ---------- Campaign headline ----------
  ctx.textAlign = 'center';
  ctx.textBaseline = 'alphabetic';

  ctx.fillStyle = MB_COLORS.magenta;
  ctx.font = `700 20px ${CARD_FONTS.pixel}`;
  ctx.letterSpacing = '0.5px';
  ctx.fillText('We already wrote the check', 540, 172);
  ctx.letterSpacing = '0px';

  ctx.fillStyle = MB_COLORS.ink;
  ctx.font = `700 60px ${CARD_FONTS.display}`;
  ctx.fillText('It just needs', 540, 244);
  ctx.font = `700 60px ${CARD_FONTS.display}`;
  ctx.fillStyle = MB_COLORS.magenta;
  ctx.fillText('your name on it.', 540, 304);
  ctx.fillStyle = MB_COLORS.ink;

  // =================================================================
  //  THE BIG CHECK — drawn into a slightly tilted frame.
  // =================================================================
  const cx = 70, cy = 352, cw = 940, ch = 356;
  const checkCX = cx + cw / 2;
  const checkCY = cy + ch / 2;
  const tilt = -0.028; // ~1.6deg counter-clockwise

  ctx.save();
  ctx.translate(checkCX, checkCY);
  ctx.rotate(tilt);
  ctx.translate(-checkCX, -checkCY);

  // Check body: hard offset shadow + THICK solid color border to the edge
  // (aqua "security" band), then the white check face inside it.
  const bw = 18;
  ctx.fillStyle = MB_COLORS.ink;
  ctx.fillRect(cx + 12, cy + 12, cw, ch);
  ctx.fillStyle = MB_COLORS.aqua;
  ctx.fillRect(cx, cy, cw, ch);
  ctx.fillStyle = MB_COLORS.chalk;
  ctx.fillRect(cx + bw, cy + bw, cw - 2 * bw, ch - 2 * bw);
  ctx.strokeStyle = MB_COLORS.ink;
  ctx.lineWidth = 3;
  ctx.strokeRect(cx, cy, cw, ch);
  ctx.lineWidth = 1.5;
  ctx.strokeRect(cx + bw, cy + bw, cw - 2 * bw, ch - 2 * bw);

  const L = cx + 38;
  const R = cx + cw - 38;

  // Top-left: enlarged gnf logo only (no wordmark) + slim tagline.
  const ls = 82;
  if (logoImg) {
    ctx.drawImage(logoImg, L, cy + 26, ls, ls);
  }
  ctx.textAlign = 'left';
  ctx.textBaseline = 'alphabetic';
  ctx.fillStyle = MB_COLORS.ink60;
  ctx.font = `500 20px ${CARD_FONTS.serif}`;
  ctx.fillText('Neighbors funding neighbors', L + ls + 18, cy + 60);
  ctx.font = `700 10px ${CARD_FONTS.pixel}`;
  ctx.letterSpacing = '0.5px';
  ctx.fillText('EST. 2023', L + ls + 18, cy + 82);
  ctx.letterSpacing = '0px';

  // Top-right: check No. + DATE
  ctx.textAlign = 'right';
  ctx.fillStyle = MB_COLORS.ink60;
  ctx.font = `700 12px ${CARD_FONTS.pixel}`;
  ctx.letterSpacing = '0.5px';
  ctx.fillText('No. 1000', R, cy + 44);
  ctx.letterSpacing = '0px';

  ctx.textAlign = 'left';
  ctx.fillStyle = MB_COLORS.ink60;
  ctx.font = `700 11px ${CARD_FONTS.pixel}`;
  const dateLabelX = R - 220;
  ctx.fillText('DATE', dateLabelX, cy + 80);
  ctx.strokeStyle = MB_COLORS.ink;
  ctx.lineWidth = 1.5;
  ctx.beginPath();
  ctx.moveTo(dateLabelX + 44, cy + 82);
  ctx.lineTo(R, cy + 82);
  ctx.stroke();
  ctx.fillStyle = MB_COLORS.ink;
  ctx.font = `400 22px ${CARD_FONTS.numeral}`;
  ctx.textAlign = 'center';
  ctx.fillText(dateStr, (dateLabelX + 44 + R) / 2, cy + 76);

  // PAY TO THE ORDER OF + payee + amount box
  const payY = cy + 172;
  ctx.textAlign = 'left';
  ctx.textBaseline = 'alphabetic';
  ctx.fillStyle = MB_COLORS.ink;
  ctx.font = `700 12px ${CARD_FONTS.pixel}`;
  ctx.letterSpacing = '1px';
  ctx.fillText('PAY TO THE', L, payY - 18);
  ctx.fillText('ORDER OF', L, payY - 2);
  ctx.letterSpacing = '0px';

  // Single centered string so the "$" never collides with the digits.
  const boxW = 184, boxH = 54, boxX = R - boxW, boxY = payY - 42;
  ctx.fillStyle = MB_COLORS.chalk;
  ctx.fillRect(boxX, boxY, boxW, boxH);
  ctx.strokeStyle = MB_COLORS.ink;
  ctx.lineWidth = 3;
  ctx.strokeRect(boxX, boxY, boxW, boxH);
  ctx.fillStyle = MB_COLORS.ink;
  ctx.font = `700 30px ${CARD_FONTS.numeral}`;
  ctx.textAlign = 'center';
  ctx.fillText('$1,000.00', boxX + boxW / 2, boxY + 37);

  const lineStartX = L + 88;
  const lineEndX = boxX - 24;
  ctx.strokeStyle = MB_COLORS.ink;
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(lineStartX, payY + 4);
  ctx.lineTo(lineEndX, payY + 4);
  ctx.stroke();

  // Payee = Your Business Here — magenta Spectral 600, mixed case, auto-shrunk to fit.
  ctx.textAlign = 'left';
  ctx.fillStyle = MB_COLORS.magenta;
  const payee = 'Your Business Here';
  let payeeSize = 44;
  ctx.font = `600 ${payeeSize}px ${CARD_FONTS.serif}`;
  const payeeAvail = lineEndX - (lineStartX + 8) - 6;
  while (ctx.measureText(payee).width > payeeAvail && payeeSize > 28) {
    payeeSize -= 2;
    ctx.font = `600 ${payeeSize}px ${CARD_FONTS.serif}`;
  }
  ctx.fillText(payee, lineStartX + 8, payY - 4);

  // Written-amount line
  const writeY = payY + 58;
  ctx.fillStyle = MB_COLORS.ink;
  ctx.font = `500 24px ${CARD_FONTS.serif}`;
  ctx.textAlign = 'left';
  const amountWords = 'One thousand and 00/100';
  ctx.fillText(amountWords, L, writeY);
  const awWidth = ctx.measureText(amountWords).width;
  ctx.fillStyle = MB_COLORS.ink60;
  const dashStart = L + awWidth + 12;
  const dollarsLabelW = 80;
  const dashEnd = R - dollarsLabelW;
  ctx.font = `400 22px ${CARD_FONTS.serif}`;
  let dxp = dashStart;
  while (dxp < dashEnd - 10) { ctx.fillText('—', dxp, writeY); dxp += 16; }
  ctx.fillStyle = MB_COLORS.ink;
  ctx.font = `700 13px ${CARD_FONTS.pixel}`;
  ctx.letterSpacing = '1px';
  ctx.textAlign = 'right';
  ctx.fillText('DOLLARS', R, writeY - 2);
  ctx.letterSpacing = '0px';

  // Bottom: MEMO (left) + signature (right)
  const botY = cy + ch - 52;

  ctx.textAlign = 'left';
  ctx.fillStyle = MB_COLORS.ink60;
  ctx.font = `700 11px ${CARD_FONTS.pixel}`;
  ctx.textBaseline = 'middle';
  ctx.fillText('MEMO', L, botY - 9);
  ctx.textBaseline = 'alphabetic';
  const memoLabelW = 52;
  ctx.strokeStyle = MB_COLORS.ink;
  ctx.lineWidth = 1.5;
  ctx.beginPath();
  ctx.moveTo(L + memoLabelW, botY);
  ctx.lineTo(L + 360, botY);
  ctx.stroke();
  ctx.fillStyle = MB_COLORS.ink;
  ctx.font = `500 22px ${CARD_FONTS.serif}`;
  ctx.fillText('Belief capital', L + memoLabelW + 8, botY - 6);

  // Signature — "Chapter Director" signed ON the line (no GNF wordmark here).
  const sigRight = R;
  const sigLeft = R - 300;
  ctx.strokeStyle = MB_COLORS.ink;
  ctx.lineWidth = 1.5;
  ctx.beginPath();
  ctx.moveTo(sigLeft, botY);
  ctx.lineTo(sigRight, botY);
  ctx.stroke();
  ctx.textAlign = 'center';
  ctx.fillStyle = MB_COLORS.ink;
  ctx.font = `600 38px ${CARD_FONTS.serif}`;
  ctx.fillText('Chapter Director', (sigLeft + sigRight) / 2, botY - 7);
  ctx.font = `700 10px ${CARD_FONTS.pixel}`;
  ctx.fillStyle = MB_COLORS.ink60;
  ctx.letterSpacing = '0.5px';
  ctx.fillText('AUTHORIZED SIGNATURE', (sigLeft + sigRight) / 2, botY + 18);
  ctx.letterSpacing = '0px';

  // "$1,000 BELIEF CAPITAL" sticker tag, overhanging the top-right corner.
  ctx.save();
  const tagW = 188, tagH = 56;
  const tagX = cx + cw - tagW + 6;
  const tagY = cy - 30;
  ctx.translate(tagX + tagW / 2, tagY + tagH / 2);
  ctx.rotate(-0.05);
  drawShadowRect(ctx, -tagW / 2, -tagH / 2, tagW, tagH, MB_COLORS.magenta, 6);
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillStyle = MB_COLORS.chalk;
  ctx.font = `700 30px ${CARD_FONTS.display}`;
  ctx.fillText('$1,000', 0, -8);
  ctx.font = `700 10px ${CARD_FONTS.pixel}`;
  ctx.letterSpacing = '0.5px';
  ctx.fillText('Belief capital', 0, 14);
  ctx.letterSpacing = '0px';
  ctx.textBaseline = 'alphabetic';
  ctx.restore();

  ctx.restore(); // end check tilt — page-level layers below stay straight

  // CTA bar BELOW the check
  const ctaY = 828, ctaH = 96;
  drawShadowRect(ctx, 70, ctaY, 940, ctaH, MB_COLORS.ink, 8);
  ctx.textAlign = 'left';
  ctx.textBaseline = 'middle';
  ctx.fillStyle = MB_COLORS.butter;
  ctx.font = `700 16px ${CARD_FONTS.pixel}`;
  ctx.letterSpacing = '0.5px';
  ctx.fillText(`${quarterLabel} closes ${deadlineStr}`, 100, ctaY + 34);
  ctx.letterSpacing = '0px';
  ctx.fillStyle = MB_COLORS.chalk;
  ctx.font = `700 30px ${CARD_FONTS.display}`;
  ctx.fillText('Submit your 60-second pitch', 100, ctaY + 66);

  const pillW = 224, pillH = 56, pillX = 1010 - 24 - pillW, pillY = ctaY + (ctaH - pillH) / 2;
  drawShadowRect(ctx, pillX, pillY, pillW, pillH, MB_COLORS.magenta, 6);
  ctx.textAlign = 'center';
  ctx.fillStyle = MB_COLORS.chalk;
  ctx.font = `700 17px ${CARD_FONTS.pixel}`;
  ctx.letterSpacing = '1px';
  ctx.fillText('APPLY ▸', pillX + pillW / 2, pillY + pillH / 2 - 9);
  ctx.letterSpacing = '0px';
  ctx.font = `600 16px ${CARD_FONTS.content}`;
  ctx.fillText('goodneighbor.fund/pitch', pillX + pillW / 2, pillY + pillH / 2 + 15);
  ctx.textBaseline = 'alphabetic';

  drawFooter(ctx, logoImg);
};

// ---------- 9. Desktop Card — the NeighborhoodOS desktop itself ----------
// The whole retro-OS interface as the share image: cloud wallpaper, desktop
// icons, the Submit-pitch window in chrome with a gradient titlebar, an open
// Start menu, and the bottom taskbar. Chrome hexes mirror win95-tokens.css
// the same way MB_COLORS mirrors theme-tokens.css.
export const CHROME_COLORS = {
  face:      '#d6ccd2',
  light:     '#eae2e7',
  highlight: '#ffffff',
  shade:     '#9c8c95',
  outline:   '#141419',
  zebra:     '#fbf7ee',
  titlebarA: '#c21d61',
  titlebarB: '#e93a7d',
  titlebarC: '#f6b3cd',
};

// Win98 bevel: face fill, 2px light top-left, 2px shade bottom-right,
// 1px ink outline. sunken flips the light/shade pair.
export const drawBevelRect = (ctx, x, y, w, h, { face = CHROME_COLORS.face, sunken = false } = {}) => {
  ctx.fillStyle = face;
  ctx.fillRect(x, y, w, h);
  ctx.fillStyle = sunken ? CHROME_COLORS.shade : CHROME_COLORS.highlight;
  ctx.fillRect(x, y, w, 2);
  ctx.fillRect(x, y, 2, h);
  ctx.fillStyle = sunken ? CHROME_COLORS.highlight : CHROME_COLORS.shade;
  ctx.fillRect(x, y + h - 2, w, 2);
  ctx.fillRect(x + w - 2, y, 2, h);
  ctx.strokeStyle = CHROME_COLORS.outline;
  ctx.lineWidth = 1;
  ctx.strokeRect(x + 0.5, y + 0.5, w - 1, h - 1);
};

export const drawDesktopCard = async (canvas, user) => {
  await waitForCardFonts();
  const ctx = canvas.getContext('2d');

  ctx.fillStyle = MB_COLORS.paper;
  ctx.fillRect(0, 0, 1080, 1080);

  drawMasthead(ctx, 'Boot up your big idea');

  const [wallpaperImg, logoImg] = await Promise.all([
    loadImage('/assets/wallpaper-clouds.webp'),
    loadImage('/assets/gnf-logo.png'),
  ]);

  // ---- Desktop field (cover-cropped wallpaper) ----
  const deskY = 120, deskH = 776; // taskbar starts at 896
  ctx.save();
  ctx.beginPath();
  ctx.rect(0, deskY, 1080, deskH);
  ctx.clip();
  if (wallpaperImg) {
    const iw = wallpaperImg.naturalWidth || wallpaperImg.width;
    const ih = wallpaperImg.naturalHeight || wallpaperImg.height;
    const scale = Math.max(1080 / iw, deskH / ih);
    const dw = iw * scale, dh = ih * scale;
    ctx.drawImage(wallpaperImg, (1080 - dw) / 2, deskY + (deskH - dh) / 2, dw, dh);
  } else {
    ctx.fillStyle = MB_COLORS.magentaSoft;
    ctx.fillRect(0, deskY, 1080, deskH);
  }
  ctx.restore();

  // ---- Desktop icons (top-left column) ----
  const icons = [
    { label: 'My Chapter', fill: MB_COLORS.aqua, deep: MB_COLORS.aquaDeep },
    { label: 'Awardee Map', fill: MB_COLORS.butter, deep: MB_COLORS.butterDeep },
    { label: 'Buddy Messenger', fill: MB_COLORS.grape, deep: MB_COLORS.grapeDeep },
  ];
  icons.forEach((icon, i) => {
    const ix = 96, iy = deskY + 52 + i * 118;
    ctx.fillStyle = MB_COLORS.ink;
    ctx.fillRect(ix - 22 + 4, iy + 4, 44, 44);
    ctx.fillStyle = icon.fill;
    ctx.fillRect(ix - 22, iy, 44, 44);
    ctx.strokeStyle = MB_COLORS.ink;
    ctx.lineWidth = 2;
    ctx.strokeRect(ix - 22, iy, 44, 44);
    ctx.fillStyle = icon.deep;
    ctx.fillRect(ix - 22 + 8, iy + 24, 28, 12);
    ctx.textAlign = 'center';
    ctx.fillStyle = MB_COLORS.chalk;
    ctx.font = `700 15px ${CARD_FONTS.content}`;
    ctx.shadowColor = 'rgba(0, 0, 0, 0.85)';
    ctx.shadowOffsetX = 1;
    ctx.shadowOffsetY = 1;
    ctx.fillText(icon.label, ix, iy + 66);
    ctx.shadowColor = 'transparent';
    ctx.shadowOffsetX = 0;
    ctx.shadowOffsetY = 0;
  });

  // ---- Submit-pitch window (chrome frame + gradient titlebar) ----
  const wx = 320, wy = deskY + 56, ww = 668, wh = 430;
  ctx.fillStyle = 'rgba(0, 0, 0, 0.35)';
  ctx.fillRect(wx + 8, wy + 8, ww, wh);
  drawBevelRect(ctx, wx, wy, ww, wh, { face: CHROME_COLORS.face });

  const tbX = wx + 5, tbY = wy + 5, tbW = ww - 10, tbH = 44;
  const grad = ctx.createLinearGradient(tbX, 0, tbX + tbW, 0);
  grad.addColorStop(0, CHROME_COLORS.titlebarA);
  grad.addColorStop(0.58, CHROME_COLORS.titlebarB);
  grad.addColorStop(1, CHROME_COLORS.titlebarC);
  ctx.fillStyle = grad;
  ctx.fillRect(tbX, tbY, tbW, tbH);
  ctx.textAlign = 'left';
  ctx.textBaseline = 'middle';
  ctx.fillStyle = MB_COLORS.chalk;
  ctx.font = `700 21px ${CARD_FONTS.content}`;
  ctx.shadowColor = 'rgba(0, 0, 0, 0.45)';
  ctx.shadowOffsetX = 1;
  ctx.shadowOffsetY = 1;
  ctx.fillText('Submit pitch — Good Neighbor Fund', tbX + 14, tbY + tbH / 2 + 1);
  ctx.shadowColor = 'transparent';
  ctx.shadowOffsetX = 0;
  ctx.shadowOffsetY = 0;
  ['_', '□', '×'].forEach((sym, i) => {
    const bx = tbX + tbW - 8 - (3 - i) * 38;
    drawBevelRect(ctx, bx, tbY + 7, 34, 30, { face: CHROME_COLORS.face });
    ctx.fillStyle = CHROME_COLORS.outline;
    ctx.textAlign = 'center';
    ctx.font = `700 17px ${CARD_FONTS.content}`;
    ctx.fillText(sym, bx + 17, tbY + 22);
  });

  // Window body (chalk content well)
  const bodyX = tbX, bodyY = tbY + tbH + 4, bodyW = tbW, bodyH = wh - tbH - 14;
  ctx.fillStyle = MB_COLORS.chalk;
  ctx.fillRect(bodyX, bodyY, bodyW, bodyH);
  ctx.strokeStyle = CHROME_COLORS.shade;
  ctx.lineWidth = 1;
  ctx.strokeRect(bodyX + 0.5, bodyY + 0.5, bodyW - 1, bodyH - 1);

  ctx.textAlign = 'left';
  ctx.textBaseline = 'alphabetic';
  ctx.fillStyle = MB_COLORS.magentaDeep;
  ctx.font = `700 17px ${CARD_FONTS.content}`;
  ctx.letterSpacing = '0.5px';
  ctx.fillText('One sentence is enough', bodyX + 30, bodyY + 52);
  ctx.letterSpacing = '0px';

  ctx.fillStyle = MB_COLORS.ink;
  ctx.font = `700 44px ${CARD_FONTS.display}`;
  ctx.fillText("What's your ", bodyX + 30, bodyY + 110);
  const wyW = ctx.measureText("What's your ").width;
  ctx.fillStyle = MB_COLORS.magenta;
  ctx.fillText('big idea?', bodyX + 30 + wyW, bodyY + 110);

  // Sunken input with typed text + cursor
  const fX = bodyX + 30, fY = bodyY + 140, fW = bodyW - 60, fH = 70;
  drawBevelRect(ctx, fX, fY, fW, fH, { face: MB_COLORS.paper, sunken: true });
  ctx.fillStyle = MB_COLORS.ink;
  ctx.font = `400 23px ${CARD_FONTS.content}`;
  ctx.textBaseline = 'middle';
  const typed = 'I want to open a bakery on my block';
  ctx.fillText(typed, fX + 18, fY + fH / 2 + 1);
  ctx.fillRect(fX + 18 + ctx.measureText(typed).width + 6, fY + 18, 3, fH - 36);
  ctx.textBaseline = 'alphabetic';

  // Helper line + magenta CTA button
  ctx.fillStyle = MB_COLORS.ink60;
  ctx.font = `400 17px ${CARD_FONTS.content}`;
  ctx.fillText('No pitch deck. No equity. Just belief.', fX, fY + fH + 40);

  const btnW = 250, btnH = 58, btnX = bodyX + bodyW - 30 - btnW, btnY = bodyY + bodyH - 30 - btnH;
  drawBevelRect(ctx, btnX, btnY, btnW, btnH, { face: MB_COLORS.magenta });
  ctx.fillStyle = MB_COLORS.chalk;
  ctx.font = `700 21px ${CARD_FONTS.content}`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText('Submit Your Pitch', btnX + btnW / 2, btnY + btnH / 2 + 1);
  ctx.textBaseline = 'alphabetic';

  // ---- Start menu (open, rising off the taskbar) ----
  const mX = 64, mW = 340, mY = deskY + 336, mH = 440; // bottom lands on the taskbar
  ctx.fillStyle = 'rgba(0, 0, 0, 0.35)';
  ctx.fillRect(mX + 8, mY + 8, mW, mH);
  drawBevelRect(ctx, mX, mY, mW, mH, { face: CHROME_COLORS.face });

  // Vertical banner
  const banW = 44;
  ctx.fillStyle = MB_COLORS.magenta;
  ctx.fillRect(mX + 5, mY + 5, banW, mH - 10);
  ctx.save();
  ctx.translate(mX + 5 + banW / 2, mY + mH - 24);
  ctx.rotate(-Math.PI / 2);
  ctx.fillStyle = MB_COLORS.chalk;
  ctx.font = `700 24px ${CARD_FONTS.content}`;
  ctx.textAlign = 'left';
  ctx.textBaseline = 'middle';
  ctx.fillText('NeighborhoodOS', 0, 1);
  ctx.restore();
  ctx.textBaseline = 'alphabetic';

  const items = [
    { label: 'Neighborhood Navigator', fill: MB_COLORS.aqua },
    { label: 'Submit Pitch', fill: MB_COLORS.magenta, active: true },
    { label: 'Awardee Map', fill: MB_COLORS.butter },
    { label: 'Buddy Messenger', fill: MB_COLORS.grape },
    { label: 'LP Portal', fill: MB_COLORS.tangerine },
    { label: 'Donate', fill: MB_COLORS.aquaDeep },
  ];
  const itemX = mX + 5 + banW, itemW = mW - 10 - banW;
  items.forEach((item, i) => {
    const iy = mY + 5 + i * 71.5;
    if (item.active) {
      ctx.fillStyle = MB_COLORS.magenta;
      ctx.fillRect(itemX, iy, itemW, 71.5);
    }
    ctx.fillStyle = item.fill;
    ctx.fillRect(itemX + 18, iy + 22, 28, 28);
    ctx.strokeStyle = MB_COLORS.ink;
    ctx.lineWidth = 2;
    ctx.strokeRect(itemX + 18, iy + 22, 28, 28);
    ctx.fillStyle = item.active ? MB_COLORS.chalk : CHROME_COLORS.outline;
    ctx.font = `600 20px ${CARD_FONTS.content}`;
    ctx.textAlign = 'left';
    ctx.textBaseline = 'middle';
    ctx.fillText(item.label, itemX + 62, iy + 37);
  });
  ctx.textBaseline = 'alphabetic';

  // ---- Taskbar ----
  const tkY = 896, tkH = 64;
  ctx.fillStyle = CHROME_COLORS.face;
  ctx.fillRect(0, tkY, 1080, tkH);
  ctx.fillStyle = CHROME_COLORS.highlight;
  ctx.fillRect(0, tkY, 1080, 2);

  drawBevelRect(ctx, 12, tkY + 10, 132, 44, { face: CHROME_COLORS.face, sunken: true });
  if (logoImg) ctx.drawImage(logoImg, 26, tkY + 21, 24, 24);
  ctx.fillStyle = CHROME_COLORS.outline;
  ctx.font = `700 21px ${CARD_FONTS.content}`;
  ctx.textAlign = 'left';
  ctx.textBaseline = 'middle';
  ctx.fillText('Start', 60, tkY + tkH / 2 + 1);

  drawBevelRect(ctx, 158, tkY + 10, 250, 44, { face: CHROME_COLORS.zebra, sunken: true });
  ctx.fillStyle = CHROME_COLORS.outline;
  ctx.font = `700 19px ${CARD_FONTS.content}`;
  ctx.fillText('Submit pitch', 176, tkY + tkH / 2 + 1);

  const trayW = 264;
  drawBevelRect(ctx, 1080 - 12 - trayW, tkY + 10, trayW, 44, { face: CHROME_COLORS.face, sunken: true });
  ctx.fillStyle = CHROME_COLORS.outline;
  ctx.font = `500 19px ${CARD_FONTS.numeral}`;
  ctx.textAlign = 'center';
  ctx.fillText('12:00 AM · 12/31/03', 1080 - 12 - trayW / 2, tkY + tkH / 2 + 1);
  ctx.textBaseline = 'alphabetic';

  drawFooter(ctx, logoImg);
};
